import { Link, Navigate } from 'react-router-dom';
import type { CtaSlot } from '../../analytics/events.js';
import { AppVersion } from '../../components/AppVersion.js';
import { Brand } from '../../components/Brand.js';
import { CodeCells } from '../../components/CodeCells.js';
import { JoinCodeForm } from '../../components/JoinCodeForm.js';
import { LegalLinks } from '../../components/LegalLinks.js';
import { LiveDemo } from '../../components/LiveDemo.js';
import { useAuth } from '../../lib/auth.js';
import { FAQ } from '../../seo/faq.js';
import { COMPARISONS, USE_CASES } from '../../seo/marketingContent.js';
import { SignupLink } from '../../components/SignupLink.js';

/** The code on the stage band. Matches LiveDemo's so the page shows one poll. */
const STAGE_CODE = 'GK7T2M';

const BEATS = [
  {
    cue: 'Build it',
    line: 'Questions grouped into screens, in your colors, with your logo.',
  },
  {
    cue: 'Open the room',
    line: 'Six characters on the big screen. Your audience needs no app and no account.',
  },
  {
    cue: 'Watch it land',
    line: 'Every vote reaches the screen in under two seconds. Nothing to refresh.',
  },
] as const;

const FEATURES = [
  {
    title: 'Screens and layout',
    body: 'One to three questions per screen, two to ten options each, one to three columns on the big screen.',
  },
  {
    title: 'Your colours and logo',
    body: 'A theme that travels to the projector and to every phone.',
  },
  {
    // The stage band above already makes the no-app claim; this covers the
    // half of it that band does not — who the room has to be to vote.
    title: 'Anonymous by default',
    body: 'Nobody in the room signs in or hands over an email. Votes are counted, not attributed.',
  },
  {
    title: 'Follow-ups',
    body: 'A message, a link, or both, sent only to the device that picked that option.',
  },
  {
    title: 'Results that keep',
    body: 'Votes accumulate across presentations. Each run is kept separately, and you can export them as CSV.',
  },
  {
    title: 'Your team',
    body: 'Workspaces, invitations, and roles for who edits and who presents.',
  },
] as const;

/**
 * One filled control per call to action.
 *
 * Sign in stays a text link here because the header already offers it as one;
 * two ink buttons side by side made the page ask for two things at once.
 *
 * `slot` is passed in rather than fixed: this renders twice, at the top of the
 * pitch and at the bottom of it, and which of the two a reader acts on is the
 * comparison the page is actually making.
 */
function Actions({ slot }: { slot: CtaSlot }) {
  return (
    <div className="landing-cta">
      <SignupLink className="button" slot={slot}>
        Get started
      </SignupLink>
      <Link to="/signin" className="landing-cta-quiet">
        Sign in
      </Link>
    </div>
  );
}

/**
 * Public face of the product at `/`.
 *
 * Laid out as a run of the show: the seam that cuts the mark cuts the page
 * too, vertically through the hero and horizontally between the acts. House
 * acts are your side of it — the pitch, the running order, the feature list.
 * The one stage act is the room's side, and it is the only place the page
 * goes black, because that is the only claim the audience experiences.
 *
 * GuestOnly in spirit but not in code: GuestOnly renders its children while
 * auth is loading, which on this route would paint marketing for every
 * returning user before bouncing them to /polls. The loading placeholder
 * matches RequireAuth.
 */
export function Landing() {
  const { status } = useAuth();

  if (status === 'loading') {
    return (
      <div className="center-page">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (status === 'signed-in') {
    return <Navigate to="/polls" replace />;
  }

  return (
    <div className="landing">
      <header className="landing-header">
        <Brand to="/" />
        <nav className="landing-nav" aria-label="Account">
          <Link to="/signin">Sign in</Link>
          <SignupLink className="button" slot="nav">
            Get started
          </SignupLink>
        </nav>
      </header>

      <section className="landing-hero">
        <div className="landing-hero-house">
          <p className="eyebrow">Live polls for the room in front of you</p>
          <h1>Ask the room. Watch it answer.</h1>
          <p className="landing-lede">
            Build a poll, put it on the big screen, and watch the room vote from their phones.
            Nothing to install, nothing to refresh.
          </p>
          <Actions slot="hero" />
          {/* The other door. Most people with a code arrive at /j/CODE from the
              projector, so this is the mistyped-code fallback — a footnote to
              the pitch, kept below a rule rather than beside the button. */}
          <div className="landing-join">
            <p className="landing-join-label">Here for a poll? Enter your code.</p>
            <JoinCodeForm />
          </div>
        </div>
        <aside className="stage-panel landing-hero-stage">
          <div className="stage-inner">
            <LiveDemo size="lg" />
          </div>
        </aside>
      </section>

      <section className="landing-act">
        <div className="landing-act-inner">
          <h2>How it works</h2>
          <ol className="landing-beats">
            {BEATS.map((beat) => (
              <li key={beat.cue} className="stage-cue">
                <p className="eyebrow">{beat.cue}</p>
                <p className="stage-cue-line">{beat.line}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* The room's side of the seam, and the only black on the page: the
          projector, as the audience meets it. Amber stays where it always is —
          inside the lit cells, which are a live artifact. */}
      <section className="stage-panel landing-room">
        <div className="landing-act-inner landing-room-inner">
          <div className="landing-room-copy">
            <p className="eyebrow">What the room sees</p>
            <h2>Six characters is the whole setup.</h2>
            <p className="landing-room-line">
              The code goes up on the big screen. Everyone watching is one tap from voting — any
              browser, no app, no account.
            </p>
          </div>
          {/* Hidden for the same reason LiveDemo hides itself: the code is a
              fixture, and CodeCells labels itself "Join code G K 7 T 2 M" —
              announced, it offers assistive tech a poll that does not exist.
              The sentence beside it carries the claim in words. */}
          <div className="landing-room-screen" aria-hidden="true">
            <p className="join-url">floorpoll.app/j/{STAGE_CODE}</p>
            <CodeCells code={STAGE_CODE} size="lg" />
          </div>
        </div>
      </section>

      <section className="landing-act">
        <div className="landing-act-inner">
          <h2>What you get</h2>
          <ul className="landing-features">
            {FEATURES.map((feature) => (
              <li key={feature.title}>
                <h3>{feature.title}</h3>
                <p>{feature.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* The only links into the comparison and use-case pages. Without them
          those pages are orphans: in the sitemap, reachable by URL, and linked
          from nowhere on the site itself. */}
      <section className="landing-act">
        <div className="landing-act-inner">
          <h2>Whose room is it?</h2>
          <ul className="landing-links">
            {USE_CASES.map((page) => (
              <li key={page.path}>
                <Link to={page.path}>{page.heading}</Link>
              </li>
            ))}
          </ul>
          <p className="landing-links-aside">
            Weighing it up against something you already use?{' '}
            {/* Names alone, comma-separated. This read "FloorPoll and X or
                FloorPoll and Y" while there were two comparisons; at six it
                was a run-on sentence rather than a list. */}
            {COMPARISONS.map((page, index) => (
              <span key={page.path}>
                {index === 0 ? '' : index === COMPARISONS.length - 1 ? ' or ' : ', '}
                <Link to={page.path}>{page.competitor}</Link>
              </span>
            ))}
            .
          </p>
        </div>
      </section>

      {/* Rendered from the same list as the FAQPage structured data in the
          built head. Google requires the two to match, and editing one and
          forgetting the other is how that normally breaks. */}
      <section className="landing-act">
        <div className="landing-act-inner">
          <h2>Questions the room usually asks</h2>
          <dl className="landing-faq">
            {FAQ.map((item) => (
              <div key={item.question}>
                <dt>{item.question}</dt>
                <dd>{item.answer}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="landing-act landing-close">
        <div className="landing-act-inner">
          <h2>Put a question in front of the room.</h2>
          <Actions slot="footer" />
          <p className="landing-price">
            Free for a room of up to 100. Paid plans stop counting rather than counting higher.
          </p>
        </div>
      </section>

      <footer className="landing-footer">
        <LegalLinks />
        <AppVersion />
      </footer>
    </div>
  );
}
